import { Link } from "react-router-dom";
import { ArrowRight, Handshake, PiggyBank, CalendarClock, Wallet } from "lucide-react";
import Reveal from "@/components/Reveal";

const BENEFITS = [
  {
    icon: CalendarClock,
    title: "Start Earlier",
    text: "A custom designed operating or financing lease package may allow you to begin your project earlier than expected.",
  },
  {
    icon: Wallet,
    title: "Preserve Capital",
    text: "Preserve current funds for business operations instead of tying them up in a conventional construction budget.",
  },
  {
    icon: PiggyBank,
    title: "Lower Total Cost",
    text: "Modular construction is very cost competitive — and in most cases costs less — with additional savings from reduced construction interest costs thanks to quicker completion.",
  },
];

const Financing = () => {
  return (
    <div data-testid="financing-page">
      <section className="relative overflow-hidden bg-[#001529]">
        <img src="/images/ext-office-metal.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-35" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#001529] via-[#00203d]/85 to-[#00203d]/50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-12 lg:pt-12 lg:pb-16">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-500 mb-4">Flexible Financing</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-tight max-w-3xl">
              Financing that fits your business
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed">
              Vision Building Systems provides customized financial alternatives to meet each client's specific
              business needs.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pt-10 pb-16 sm:pt-12 sm:pb-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" aria-labelledby="financing-options-heading">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <Reveal className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-600 mb-4">Lease Packages</p>
            <h2 id="financing-options-heading" className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 gold-underline">
              Operating & financing lease options
            </h2>
            <div className="mt-8 space-y-4 text-base text-slate-600 leading-relaxed">
              <p>
                The use of one of our custom designed operating or financing lease packages may allow you to begin
                your project earlier than expected, while preserving current funds for business operations.
              </p>
              <p>
                Because VBS buildings are manufactured off-site while on-site preparation and foundations are
                completed, your project can finish in as little as half the time of conventional construction —
                reducing construction interest costs and getting your team into the space sooner.
              </p>
              <p>
                Talk to our team about a structure that matches your budget cycle, project timeline, and
                long-term plans for the building.
              </p>
            </div>
            <Link
              to="/contact"
              data-testid="financing-quote-button"
              className="mt-8 inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white font-bold px-7 py-3.5 rounded-md transition-colors duration-200"
            >
              Discuss financing options <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Reveal>
          <Reveal delay={120} className="lg:col-span-5">
            <div className="rounded-xl overflow-hidden shadow-xl">
              <img src="/images/ext-exec-hardilap.jpg" alt="Executive office modular building with shingle mansard" className="w-full h-72 object-cover" />
            </div>
            <div className="mt-6 rounded-xl bg-[#00203d] blueprint-grid p-6 sm:p-7">
              <div className="flex items-center gap-3">
                <Handshake className="h-8 w-8 text-amber-500" aria-hidden="true" />
                <p className="font-heading text-lg font-bold text-white">Customized financial alternatives</p>
              </div>
              <p className="mt-3 text-sm text-slate-300 leading-relaxed">
                Every package is designed around your specific business needs — no one-size-fits-all terms.
              </p>
            </div>
          </Reveal>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
          {BENEFITS.map((b, i) => (
            <Reveal key={b.title} delay={i * 90}>
              <article
                data-testid={`financing-benefit-${b.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="h-full bg-white rounded-xl p-6 sm:p-8 border border-slate-100 shadow-sm hover:shadow-xl transition-shadow duration-300"
              >
                <div className="h-12 w-12 rounded-lg bg-[#00203d] flex items-center justify-center">
                  <b.icon className="h-6 w-6 text-amber-500" aria-hidden="true" />
                </div>
                <h3 className="mt-5 font-heading text-lg sm:text-xl font-bold text-slate-900">{b.title}</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{b.text}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Financing;
